import React, { MouseEventHandler } from "react";

import { combineEventFilters } from "@formant/core/common/combineFilters";
import { IEventCounts } from "@formant/core/model/IEventCounts";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IFilter } from "@formant/core/model/IFilter";

import { useStores } from "../../../context/StoreContext";
import { notificationEventTypes } from "../../../store/SeekOption";
import { useLiveEventCounts } from "../../../utils/useLiveEventCounts";
import { Badge, HeaderButton } from "./NewDeviceHeader.styles";

interface IHeaderNotificationButtonProps {
    onClick?: MouseEventHandler;
    count: number | undefined;
    selected: boolean;
    eventType: "critical" | "error" | "warning" | "info";
}

export function HeaderNotificationButton(
    props: IHeaderNotificationButtonProps,
) {
    const { onClick, eventType, count, selected } = props;
    return (
        <HeaderButton
            icon="bell"
            onClick={onClick}
            data-tooltip="Events"
            selected={selected}
            data-testid="device-events-notification"
        >
            {count !== undefined && count > 0 && (
                <Badge type={eventType}>{count}</Badge>
            )}
        </HeaderButton>
    );
}

function eventColor(
    counts: IEventCounts | undefined,
): "critical" | "error" | "warning" | "info" {
    const { info, warning, error, critical } = {
        info: 0,
        warning: 0,
        error: 0,
        critical: 0,
        ...counts,
    };

    if (critical > 0) {
        return "critical";
    } else if (error > 0) {
        return "error";
    } else if (warning > 0) {
        return "warning";
    } else if (info > 0) {
        return "info";
    }

    return "info";
}

interface IConnectedHeaderNotificationButtonProps {
    onClick?: MouseEventHandler;
    context: IFilter;
    eventFilter: IEventFilter;
    selected: boolean;
}

export function ConnectedHeaderNotificationButton(
    props: IConnectedHeaderNotificationButtonProps,
) {
    const { context, eventFilter, onClick, selected } = props;
    const { eventStore } = useStores();

    const compositeFilter = combineEventFilters(context, {
        ...eventFilter,
        disableNullMatches: true,
        ...("notificationEnabled" in eventFilter
            ? { notificationEnabled: eventFilter.notificationEnabled }
            : { notificationEnabled: true }),
        ...("viewed" in eventFilter
            ? { viewed: eventFilter.viewed }
            : { viewed: false }),
        ...("eventTypes" in eventFilter
            ? { eventTypes: eventFilter.eventTypes }
            : {
                  eventTypes: notificationEventTypes,
              }),
    });

    const { names, notNames, ...filterWithoutNames } = compositeFilter;

    const liveEventCounts = useLiveEventCounts(eventFilter);

    const unviewedCount = eventStore.count({
        ...filterWithoutNames,
        viewed: false,
    });

    return (
        <HeaderNotificationButton
            count={unviewedCount ?? 0}
            eventType={eventColor(liveEventCounts)}
            onClick={onClick}
            selected={selected}
        />
    );
}
